import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Badge, Button } from "react-bootstrap";
import { getQuoteSignoff, setQuotePmToCheck, setQuoteSignoff, listUsers } from "../../services/apiClient.js";
import { formatDateTime } from "../../utils/date.js";
import { rowLabel, selectableRows } from "../../utils/people.js";

// Quote sign-off: mark the quote Typed, then Checked. For Standard R&Q jobs the
// status engine blocks Quoted/Accepted until both are done (the rule lives in
// services/claimStatus.js, so it holds for the inspector gateway too).
//
// "To be checked by" (1.0 QuotePage "Assign PM To Check", Karen 2026-07-19):
// once Typed, pick WHO should check — advisory routing that decides whose
// column the quote sits in on the Typed Not Checked worklist; it never limits
// who may tick Checked (1.0 behaviour). Unlike 1.0 (where the dropdown only
// existed in the postback right after typing), it stays editable the whole
// typed-not-checked window.

function QuoteSignoff({ claimId, onChanged }) {
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");

  function load() {
    getQuoteSignoff(claimId)
      .then(setS)
      .catch(() => setError("Unable to load sign-off."));
  }
  useEffect(() => {
    load();
    // The checker list = the PM population (1.0 LoadProjectManagersList).
    listUsers({ pco: true }).then(setUsers).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  async function toggle(which, value) {
    setError("");
    try {
      setS(await setQuoteSignoff(claimId, which, value));
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update sign-off.");
    }
  }

  async function assignChecker(userId) {
    setError("");
    try {
      setS(await setQuotePmToCheck(claimId, userId ? Number(userId) : null));
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to assign the checker.");
    }
  }

  // Checked waits for the PC Sums tick (KS 2026-09-08), every quote.
  const pcSumsBlock = !!s?.typed_at && !s?.pc_sums_confirmed_at;

  function row(label, at, byName, which) {
    const done = !!at;
    return (
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span style={{ minWidth: 70 }} className="fw-semibold">{label}</span>
        {done ? (
          <>
            <Badge bg="success">Done</Badge>
            <span className="text-muted small">
              by {byName || "—"} · {formatDateTime(at)}
            </span>
            <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => toggle(which, false)}>
              Clear
            </Button>
          </>
        ) : (
          <>
            <Badge bg="light" text="dark">{label === "PC Sums" ? "Cost types not confirmed" : `Not ${label.toLowerCase()}`}</Badge>
            {/* A disabled button swallows hover, so the reason it is disabled
                sits on a wrapper (Bootstrap's own advice), where the tooltip
                shows. Wording: Robert 2026-09-08. */}
            <span
              className="ms-auto d-inline-block"
              data-hint={
                which === "checked" && !s?.typed_at ? "Must mark the quote Typed above before you can mark it as checked"
                : which === "checked" && pcSumsBlock ? "Must check PC Sums above before you can mark quote as checked"
                : undefined
              }
            >
              <Button
                size="sm"
                variant="success"
                disabled={which === "checked" && (!s?.typed_at || pcSumsBlock)}
                style={which === "checked" && (!s?.typed_at || pcSumsBlock) ? { pointerEvents: "none" } : undefined}
                onClick={() => toggle(which, true)}
              >
                {label === "PC Sums" ? "PC Sum cost types entered" : `Mark ${label} by me`}
              </Button>
            </span>
          </>
        )}
      </div>
    );
  }

  // Between Typed and Checked the assignment is live routing (editable);
  // after Checked it's history (shown read-only when set).
  function checkerRow() {
    if (!s?.typed_at) return null;
    if (s.checked_at) {
      return s.pm_to_check_id ? (
        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
          <span style={{ minWidth: 70 }} className="fw-semibold">Checker</span>
          <span className="text-muted small">was assigned to {s.pm_to_check_name}</span>
        </div>
      ) : null;
    }
    return (
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span style={{ minWidth: 70 }} className="fw-semibold">Check by</span>
        <ThemedSelect
          size="sm"
          style={{ maxWidth: 220 }}
          value={s.pm_to_check_id || ""}
          onChange={(e) => assignChecker(e.target.value)}
          data-hint="Whose worklist this quote-to-check appears on — anyone may still tick Checked"
        >
          <option value="">— Not assigned —</option>
          {/* Retired accounts aren't offered, but the one this quote is already
              routed to stays listed — see utils/people.js. */}
          {selectableRows(users, s.pm_to_check_id).map((u) => (
            <option key={u.id} value={u.id}>{rowLabel(u)}</option>
          ))}
        </ThemedSelect>
        <span className="text-muted small">routes it to their Typed Not Checked list</span>
      </div>
    );
  }

  return (
    <div>
      <h5 className="mb-1">Quote Sign-off</h5>
      <p className="text-muted small mb-3">
        Standard R&amp;Q jobs must have the quote <strong>Typed</strong> and{" "}
        <strong>Checked</strong> before they can be Quoted or Accepted.
      </p>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {s ? (
        <>
          {row("Typed", s.typed_at, s.typed_by_name, "typed")}
          {/* KS 2026-08-25: the typist attests PC Sum COST TYPES are entered
              on every job; since 2026-09-08 Checked waits for it (pcSumsBlock).
              The wording badge catches labelled misses; this tick is for the
              unlabelled ones. (The pre-tick "not tracked" display went with the
              gate: an old quote still to be checked needs the tick to press.) */}
          {row("PC Sums", s.pc_sums_confirmed_at, s.pc_sums_confirmed_by_name, "pc-sums")}
          {checkerRow()}
          {row("Checked", s.checked_at, s.checked_by_name, "checked")}
        </>
      ) : (
        <p className="text-muted mb-0">Loading…</p>
      )}
    </div>
  );
}

export default QuoteSignoff;
